import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const UserCard = ({ user, onSwipe, onAudioToggle, isPlaying, style, isDragging }) => {
  const [audioPlaying, setAudioPlaying] = useState(false);
  const cardRef = useRef(null);

  const handleAudioToggle = () => {
    setAudioPlaying(!audioPlaying);
    onAudioToggle(user.id, !audioPlaying);
  };

  const handleSwipeLeft = () => {
    onSwipe(user.id, 'left');
  };

  const handleSwipeRight = () => {
    onSwipe(user.id, 'right');
  };

  const getCompatibilityColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-accent';
    if (score >= 40) return 'text-warning';
    return 'text-error';
  };

  return (
    <div
      ref={cardRef}
      className={`relative w-full max-w-sm mx-auto bg-surface rounded-2xl shadow-elevation-4 overflow-hidden transition-cosmic ${
        isDragging ? 'cursor-grabbing' : 'cursor-grab'
      }`}
      style={style}
    >
      {/* Profile Image */}
      <div className="relative h-96 overflow-hidden">
        <Image
          src={user.profileImage}
          alt={`${user.name}'s profile`}
          className="w-full h-full object-cover"
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent"></div>
        
        {/* Audio Control */}
        <div className="absolute top-4 right-4">
          <Button
            variant="ghost"
            iconName={audioPlaying ? "Pause" : "Play"}
            onClick={handleAudioToggle}
            className="w-12 h-12 rounded-full glass bg-surface/20 backdrop-blur-cosmic text-text-primary hover:bg-surface/40"
          />
        </div>

        {/* Compatibility Score */}
        <div className="absolute top-4 left-4 glass bg-surface/20 backdrop-blur-cosmic rounded-full px-3 py-1">
          <div className="flex items-center space-x-1">
            <Icon name="Sparkles" size={14} color="#F59E0B" />
            <span className={`text-sm font-bold ${getCompatibilityColor(user.compatibilityScore)}`}>
              {user.compatibilityScore}%
            </span>
          </div>
        </div>

        {/* User Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex items-end justify-between">
            <div>
              <h2 className="text-2xl font-heading font-bold text-text-primary mb-1">
                {user.name}, {user.age}
              </h2>
              <div className="flex items-center space-x-2 text-text-secondary text-sm">
                <Icon name="MapPin" size={14} color="currentColor" />
                <span>{user.location}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-text-secondary mb-1">Distance</div>
              <div className="text-sm font-medium text-text-primary">{user.distance}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Astrological Info */}
      <div className="p-6 space-y-4">
        {/* Zodiac Signs */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl mb-1">{user.astrology.sun.symbol}</div>
            <div className="text-xs text-text-secondary">Sun</div>
            <div className="text-sm font-medium text-text-primary">{user.astrology.sun.sign}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl mb-1">{user.astrology.moon.symbol}</div>
            <div className="text-xs text-text-secondary">Moon</div>
            <div className="text-sm font-medium text-text-primary">{user.astrology.moon.sign}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl mb-1">{user.astrology.rising.symbol}</div>
            <div className="text-xs text-text-secondary">Rising</div>
            <div className="text-sm font-medium text-text-primary">{user.astrology.rising.sign}</div>
          </div>
        </div>

        {/* Musical Tone Preview */}
        <div className="glass bg-surface/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <Icon name="Music" size={16} color="#8B5CF6" />
              <span className="text-sm font-medium text-text-primary">Current Tone</span>
            </div>
            <div className="text-xs text-text-secondary font-mono">
              {user.currentTone.key} {user.currentTone.mode}
            </div>
          </div>
          <div className="text-xs text-text-secondary">
            {user.currentTone.description}
          </div>
        </div>

        {/* Compatibility Visualization */}
        <div className="glass bg-surface/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-text-primary">Harmonic Compatibility</span>
            <span className={`text-sm font-bold ${getCompatibilityColor(user.compatibilityScore)}`}>
              {user.compatibilityScore}%
            </span>
          </div>
          
          {/* Simplified Zodiac Wheel Visualization */}
          <div className="relative w-24 h-24 mx-auto">
            <div className="absolute inset-0 border-2 border-primary/30 rounded-full"></div>
            <div className="absolute inset-2 border-2 border-secondary/30 rounded-full"></div>
            <div className="absolute inset-4 border-2 border-accent/30 rounded-full"></div>
            
            {/* Compatibility indicators */}
            {user.harmonicAspects.map((aspect, index) => (
              <div
                key={index}
                className={`absolute w-2 h-2 rounded-full ${
                  aspect.type === 'trine' ? 'bg-success' :
                  aspect.type === 'sextile' ? 'bg-accent' :
                  aspect.type === 'square'? 'bg-warning' : 'bg-error'
                }`}
                style={{
                  top: `${50 + 30 * Math.cos(aspect.angle * Math.PI / 180)}%`,
                  left: `${50 + 30 * Math.sin(aspect.angle * Math.PI / 180)}%`,
                  transform: 'translate(-50%, -50%)'
                }}
              ></div>
            ))}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-6 pb-6">
        <div className="flex justify-center space-x-6">
          <Button
            variant="ghost"
            iconName="X"
            onClick={handleSwipeLeft}
            className="w-14 h-14 rounded-full bg-error/20 text-error hover:bg-error/30 border-2 border-error/30"
          />
          <Button
            variant="ghost"
            iconName="Heart"
            onClick={handleSwipeRight}
            className="w-16 h-16 rounded-full bg-success/20 text-success hover:bg-success/30 border-2 border-success/30"
          />
        </div>
      </div>

      {/* Audio Playing Indicator */}
      {audioPlaying && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-secondary to-accent animate-gentle-pulse"></div>
      )}
    </div>
  );
};

export default UserCard;